// src/app/[lang]/components/LazyArticleImage.tsx
"use client";

import React, { useState } from 'react';
import Image from 'next/image';

interface LazyArticleImageProps {
  src: string;
  alt: string;
  width: number;
  height: number;
  blurDataURL?: string;
  caption?: string;
  priority?: boolean;
}

/**
 * Article image with smooth transition from blur to full image
 * Uses thumbhash blur placeholder while loading
 */
export default function LazyArticleImage({
  src,
  alt,
  width,
  height,
  blurDataURL,
  caption,
  priority = true,
}: LazyArticleImageProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  if (hasError) {
    return (
      <div className="w-full mt-2 p-8 bg-gray-100 rounded text-center">
        <p className="text-gray-600">Image failed to load</p>
      </div>
    );
  }

  const aspectRatio = width / height;

  return (
    <div className="mt-2">
      <div className="relative" style={{ aspectRatio }}>
        {blurDataURL && (
          <img
            src={blurDataURL}
            alt=""
            className="block w-full h-full"
            style={{ objectFit: 'cover' }}
            aria-hidden="true"
          />
        )}
        <Image
          src={src}
          alt={alt}
          width={width}
          height={height}
          priority={priority}
          loading={priority ? "eager" : "lazy"}
          quality={75}
          className={`block absolute top-0 left-0 w-full h-full print:hidden transition-opacity duration-1000 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ objectFit: 'cover' }}
          sizes="(max-width: 1050px) 100vw, 780px"
          onLoad={() => setIsLoaded(true)}
          onError={() => {
            console.error('Image failed to load:', src.substring(0, 50));
            setHasError(true);
          }}
        />
      </div>

      {caption && (
        <div className="mt-1">
          <div
            className="article-content text-sm text-gray-600 [&>:first-child]:mt-0 [&>:last-child]:mb-0"
            dangerouslySetInnerHTML={{ __html: caption }}
          />
        </div>
      )}
    </div>
  );
}
